'use client';

import { useCallback } from 'react';
import type { RoomMode } from '@/lib/acousticsPhysics';
import { findModeClusters, nearestMode } from '@/lib/tools/modeSpectrum';
import Chart, { type ChartScrub } from '@/components/room/charts/Chart';
import { drawModeSpectrum } from './modeSpectrumDraw';

interface Props {
  modes: RoomMode[];
  schroederHz: number;
  fMin?: number;
  fMax?: number;
  /** Dim modes not matching this type. */
  filterType?: 'all' | RoomMode['type'];
  /** Highlight a selected mode frequency. */
  selectedHz?: number | null;
  onSelectMode?: (mode: RoomMode) => void;
}

/**
 * Canvas log-frequency stem plot — axial / tangential / oblique, Schroeder
 * band, cluster warnings, hover + click — hosted by the room dock's
 * `Chart.tsx`. Selecting a mode (click, or drag anywhere on the plot) is
 * `Chart`'s standing-marker "scrub" concept: `onSelectMode` is the write side
 * of the selection state the caller already owns (`selectedHz`), the same
 * shape `useModalFreqScrub` uses for the frequency-response chart's own
 * marker. Hovering names the nearest mode through the shared crosshair's
 * per-axis format function (`drawModeSpectrum`'s `plot.x.format`) instead of
 * a second, hand-rolled tooltip.
 */
export default function ModeSpectrum({
  modes,
  schroederHz,
  fMin = 20,
  fMax = 250,
  filterType = 'all',
  selectedHz = null,
  onSelectMode,
}: Props) {
  const items = modes.filter((m) => m.freqHz >= fMin && m.freqHz <= fMax);
  const clusters = findModeClusters(items, 5);

  const draw = useCallback(
    (canvas: HTMLCanvasElement) => drawModeSpectrum(canvas, { modes, schroederHz, fMin, fMax, filterType, selectedHz }),
    [modes, schroederHz, fMin, fMax, filterType, selectedHz],
  );

  const scrub: ChartScrub | undefined = onSelectMode
    ? {
        // No selection yet reads as NaN, which markerAtX rejects outright —
        // the standing marker simply doesn't appear until something is
        // selected, rather than pinning to a fake in-range frequency.
        value: selectedHz ?? Number.NaN,
        onChange: (x) => {
          const m = nearestMode(items, x);
          if (m) onSelectMode(m);
        },
      }
    : undefined;

  return (
    <div className="guided-theme space-y-2" data-testid="mode-spectrum">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-[var(--rt-ink-dim)]">
        <span>Mode spectrum · log frequency · stem height ≈ relative energy</span>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--rt-accent)]" /> Axial
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[color-mix(in_srgb,var(--rt-ink)_72%,transparent)]" />{' '}
            Tangential
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[color-mix(in_srgb,var(--rt-ink)_40%,transparent)]" /> Oblique
          </span>
          <span className="inline-flex items-center gap-1 text-[var(--rt-danger)]">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[color-mix(in_srgb,var(--rt-danger)_50%,transparent)]" /> Cluster
          </span>
        </div>
      </div>

      <Chart
        title="Mode spectrum"
        hideHeader
        draw={draw}
        scrub={scrub}
        testId="mode-spectrum-canvas"
      />

      {clusters.length > 0 && (
        <p className="text-[11px] text-[var(--rt-danger)]" data-testid="mode-cluster-note">
          {clusters.length} dense cluster{clusters.length === 1 ? '' : 's'} (≤5 Hz apart) — overlapping
          resonances are harder to treat evenly.
        </p>
      )}
    </div>
  );
}
